<template>
    <div class="home-wrapper">
        <div class="home-page">
            <div class="hero-background">
                <img
                    src="@/assets/images/domme/bg_domme.avif"
                    alt="Santina"
                    class="background-image"
                />
            </div>

            <!-- Header -->
            <header class="home-header">
                <img
                    src="@/assets/images/brand/Santina.svg"
                    alt="Santina"
                    class="logo"
                />
                <div class="header-actions">
                    <div class="auth-buttons">
                        <button
                            class="nav-button login-button"
                            @click="openAuthModal"
                        >
                            {{ $t('general.login') }}
                        </button>
                        <button
                            class="nav-button signup-button"
                            @click="openAuthModal"
                        >
                            {{ $t('login.signup') }}
                        </button>
                    </div>
                    <button
                        class="nav-button creator-button"
                        @click="goToCreator"
                    >
                        {{ $t('general.go-to-creator') }}
                    </button>
                    <button
                        class="menu-button"
                        :class="{ active: isMenuOpen }"
                        @click="toggleMenu"
                        :aria-label="$t('home.creator-landing.nav.menu-aria')"
                    >
                        <span class="menu-line"></span>
                        <span class="menu-line"></span>
                        <span class="menu-line"></span>
                    </button>
                </div>
            </header>

            <div
                class="mobile-menu"
                :class="{ open: isMenuOpen }"
                @click.self="closeMenu"
            >
                <div class="mobile-menu-container">
                    <div class="mobile-menu-header">
                        <button
                            class="mobile-close-button"
                            @click="closeMenu"
                            :aria-label="
                                $t('home.creator-landing.nav.close-menu-aria')
                            "
                        >
                            <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M18 6L6 18M6 6L18 18"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                            </svg>
                        </button>
                    </div>
                    <div class="mobile-menu-language">
                        <MobileLanguageMenu />
                    </div>
                    <div class="mobile-menu-actions">
                        <div class="mobile-auth-buttons">
                            <button
                                type="button"
                                class="mobile-button mobile-menu-cta"
                                @click="handleMobileSignup"
                            >
                                {{ $t('general.join-now') }}
                            </button>
                            <button
                                type="button"
                                class="mobile-button mobile-menu-cta"
                                @click="handleMobileLogin"
                            >
                                {{ $t('general.login') }}
                            </button>
                        </div>
                        <a
                            class="mobile-creator-link"
                            href="/creator"
                            @click.prevent="handleMobileCreator"
                        >
                            {{ $t('general.start-earning') }} →
                        </a>
                    </div>
                </div>
            </div>

            <!-- Main Content -->
            <main class="home-content">
                <div class="text-content">
                    <h1 class="main-title">
                        {{ $t('landing.chat-with-creators') }}
                    </h1>
                    <p class="subtitle">
                        {{ $t('landing.subtitle-line1') }}
                    </p>
                </div>
                <div class="cta-section">
                    <PrimaryButton
                        :label="$t('landing.meet-creators')"
                        @click="openHowItWorks"
                        classProp="home-cta-button"
                        buttonType="primary"
                        labelFontSize="16px"
                        labelFontWeight="600"
                    />
                    <PrimaryButton
                        :label="$t('general.login')"
                        @click="openAuthModal"
                        classProp="home-cta-button"
                        buttonType="primary"
                        labelFontSize="16px"
                        labelFontWeight="600"
                    />
                    <p class="button-disclaimer">
                        {{ $t('landing.disclaimer') }}
                    </p>
                </div>
            </main>

            <!-- Auth Modal -->
            <GenericModal
                v-model="showAuthModal"
                :closable="!isAuthLoading"
                :fullscreenMobile="true"
                :darkMode="true"
                contentPadding="0"
            >
                <div class="auth-modal-container">
                    <Continue2
                        :darkMode="true"
                        @auth-loading="isAuthLoading = $event"
                    />
                </div>
            </GenericModal>

            <HowItWorksModal ref="howItWorksRef" />
        </div>
        <AppFooter />
    </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import MobileLanguageMenu from '@/components/shared/MobileLanguageMenu.vue'
import Continue2 from '@/components/authentication/continue2.vue'
import GenericModal from '@/components/shared/core/GenericModal.vue'
import PrimaryButton from '@/components/shared/buttons/primary-button.vue'
import AppFooter from '@/components/shared/AppFooter.vue'
import HowItWorksModal from '@/components/general-modals/how-it-works.vue'

const router = useRouter()
const showAuthModal = ref(false)
const isMenuOpen = ref(false)
const isAuthLoading = ref(false)
const howItWorksRef = ref(null)

const openHowItWorks = () => {
    if (howItWorksRef.value) {
        howItWorksRef.value.show()
    }
}

const goToCreator = () => {
    router.push('/dommes')
}

const openAuthModal = () => {
    // For organic Home signups, clear any stale client intents
    // and set signup intent to ensure they're auto-upgraded to Client.
    // Auth modal supports both login and signup, so always set intent here.
    try {
        localStorage.removeItem('clientIntent')
        localStorage.removeItem('clientTargetCreator')
        const existingSignupIntent = localStorage.getItem('signupIntent')
        const hasCreatorInvite =
            Boolean(localStorage.getItem('referralId')) ||
            Boolean(localStorage.getItem('partnerUsername')) ||
            Boolean(localStorage.getItem('studioInvite'))
        const shouldForceCreator =
            existingSignupIntent === 'creator' || hasCreatorInvite
        localStorage.setItem(
            'signupIntent',
            shouldForceCreator ? 'creator' : 'client'
        )
    } catch (e) {
        console.warn('Failed to set signup intent:', e)
    }
    showAuthModal.value = true
}

const handleMobileLogin = () => {
    closeMenu()
    openAuthModal()
}

const handleMobileSignup = () => {
    closeMenu()
    openAuthModal()
}

const handleMobileCreator = () => {
    closeMenu()
    goToCreator()
}

const toggleMenu = () => {
    isMenuOpen.value = !isMenuOpen.value
    if (isMenuOpen.value) {
        document.body.style.overflow = 'hidden'
    } else {
        document.body.style.overflow = ''
    }
}

const closeMenu = () => {
    isMenuOpen.value = false
    document.body.style.overflow = ''
}

const handleEscapeKey = (event) => {
    if (event.key === 'Escape' && isMenuOpen.value) {
        closeMenu()
    }
}

const handleResize = () => {
    if (window.innerWidth > 768 && isMenuOpen.value) {
        closeMenu()
    }
}

onMounted(() => {
    // Check for creator attribution parameters and redirect if present
    const urlParams = new URLSearchParams(window.location.search)
    const invite = urlParams.get('invite')
    const studioInvite = urlParams.get('studioInvite')
    const partner = urlParams.get('partner')
    const partnerUsername = urlParams.get('partnerUsername')
    const newRef = urlParams.get('new')
    const welcomeBonusCode = urlParams.get('code')

    // Save new reference ID directly if found
    if (newRef) {
        try {
            localStorage.setItem('referralId', newRef)
        } catch (e) {
            console.warn('Failed to persist referral ID:', e)
        }
    }

    if (
        invite ||
        studioInvite ||
        partner ||
        partnerUsername ||
        newRef ||
        welcomeBonusCode
    ) {
        router.push({
            path: '/creator',
            query: Object.fromEntries(urlParams.entries()),
        })
    }

    document.addEventListener('keydown', handleEscapeKey)
    window.addEventListener('resize', handleResize)
})

onBeforeUnmount(() => {
    // Close modals when navigating away
    showAuthModal.value = false
    closeMenu()
    document.removeEventListener('keydown', handleEscapeKey)
    window.removeEventListener('resize', handleResize)
})
</script>

<style scoped>
.home-wrapper {
    width: 100%;
    min-height: 100dvh;
}

.home-page {
    position: relative;
    width: 100%;
    height: 100dvh;
    min-height: 100dvh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

/* Header */
.home-header {
    position: relative;
    z-index: 1001;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px;
    padding-left: 25px;
    padding-top: 11px;
    width: 100%;
    box-sizing: border-box;
}

.header-actions {
    display: flex;
    align-items: center;
    gap: 12px;
}

.auth-buttons {
    display: flex;
    align-items: center;
    gap: 10px;
    padding-right: 6px;
    border-right: 1px solid rgba(255, 255, 255, 0.18);
}

.nav-button {
    padding: 8px 16px;
    border-radius: 20px;
    font-size: 14px;
    font-weight: 500;
    font-family: 'Nunito', sans-serif;
    cursor: pointer;
    transition: all 0.2s ease;
    border: none;
    white-space: nowrap;
    display: none;
}

.login-button {
    background: transparent;
    color: rgba(255, 255, 255, 0.9);
    border: 1px solid rgba(255, 255, 255, 0.3);
}

.login-button:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.5);
}

.signup-button {
    background: #c9a646;
    color: #0b0b0b;
    border: 1px solid #c9a646;
}

.signup-button:hover {
    background: #d4b456;
    border-color: #d4b456;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(201, 166, 70, 0.3);
}

.creator-button {
    background: rgba(201, 166, 70, 0.14);
    color: #c9a646;
    border: 1px solid rgba(201, 166, 70, 0.45);
}

.creator-button:hover {
    background: rgba(201, 166, 70, 0.22);
    border-color: rgba(201, 166, 70, 0.7);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(201, 166, 70, 0.2);
}

.logo {
    width: 102px;
    height: auto;
}

.menu-button {
    background: none;
    border: none;
    display: flex;
    flex-direction: column;
    gap: 5px;
    padding: 5px;
    cursor: pointer;
    transition: all 0.3s ease;
}

.menu-line {
    width: 20px;
    height: 2px;
    background: #fff;
    border-radius: 1px;
    transition: all 0.3s ease;
}

.menu-button.active .menu-line:nth-child(1) {
    transform: rotate(45deg) translate(6px, 6px);
}

.menu-button.active .menu-line:nth-child(2) {
    opacity: 0;
}

.menu-button.active .menu-line:nth-child(3) {
    transform: rotate(-45deg) translate(6px, -6px);
}

/* Main Content */

.hero-background {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 0;
    overflow: hidden;
}

.hero-background::after {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 1;
    pointer-events: none;
    background: linear-gradient(
        180deg,
        rgba(0, 0, 0, 0.62) 0%,
        rgba(0, 0, 0, 0.69) 100%
    );
}

.background-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: top;
    display: block;
    position: relative;
    z-index: 0;
}

.home-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    padding: 0 25px 27px;
    position: relative;
    z-index: 1001;
    pointer-events: none;
}

/* Mobile Menu */
.mobile-menu {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 0;
    background: linear-gradient(180deg, #0b0b0b 0%, #141414 100%);
    overflow: hidden;
    z-index: 1200;
    transition: height 0.4s ease;
}

.mobile-menu.open {
    height: 100dvh;
    overflow-y: auto;
}

.mobile-menu-container {
    padding: 80px 20px 20px;
    display: flex;
    flex-direction: column;
    opacity: 0;
    transform: translateY(20px);
    transition: all 0.3s ease;
    transition-delay: 0.2s;
    min-height: 100%;
}

.mobile-menu.open .mobile-menu-container {
    opacity: 1;
    transform: translateY(0);
}

.mobile-menu-header {
    position: fixed;
    top: 0;
    right: 0;
    padding: 15px;
    z-index: 1201;
    display: flex;
    justify-content: flex-end;
    width: 100%;
}

.mobile-close-button {
    width: 44px;
    height: 44px;
    background: transparent;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #c9a646;
    transition: transform 0.2s ease;
}

.mobile-close-button:hover {
    transform: scale(1.1);
}

.mobile-menu-language {
    margin-top: 10px;
    justify-content: center;
    width: 100%;
}

.mobile-menu-actions {
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;
    margin-top: 20px;
}

.mobile-auth-buttons {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.mobile-button {
    width: 100%;
    border: none;
    border-radius: 15px;
    font-size: 16px;
    font-weight: 600;
    padding: 13px 16px;
    font-family: 'Basier Circle', sans-serif;
    cursor: pointer;
    transition:
        background 0.2s ease,
        transform 0.2s ease,
        box-shadow 0.2s ease;
}

.mobile-menu-cta {
    background: #9b6e2b;
    color: #fff;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3);
}

.mobile-menu-cta:hover {
    background: #8a6226;
    color: #fff;
}

.mobile-menu-cta:active {
    background: #7a5822;
    transform: scale(0.98);
}

.mobile-creator-link {
    display: block;
    text-align: center;
    color: rgba(255, 255, 255, 0.6);
    font-size: 15px;
    font-family: 'Nunito', sans-serif;
    text-decoration: none;
    margin-top: 16px;
    cursor: pointer;
    transition: color 0.2s ease;
}

.mobile-creator-link:hover {
    color: #c9a646;
}

.text-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    width: 100%;
    max-width: 400px;
    pointer-events: auto;
    padding-top: 80px;
}

.cta-section {
    text-align: center;
    max-width: 400px;
    width: 100%;
    pointer-events: auto;
    display: flex;
    flex-direction: column;
    align-items: center;
}

.main-title {
    font-size: 64px;
    font-weight: 400;
    color: white;
    margin: 0 0 20px 0;
    line-height: 0.8;
    font-family: 'Meditative';
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
    text-align: center;
    text-wrap: balance;
}

.subtitle {
    font-size: 19px;
    font-weight: 400;
    color: rgba(255, 255, 255, 0.85);
    margin: 0;
    line-height: 110%;
    font-family: 'Open Sans', sans-serif;
    text-align: center;
    width: 100%;
}

.home-cta-button {
    width: 100%;
    height: 55px;
    margin-bottom: 15px;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3);
}

:deep(.home-cta-button.linear-bg) {
    background: #9b6e2b;
    border: none;
    color: #fff;
    font-family: 'Basier Circle', sans-serif;
    font-size: 16px;
    font-weight: 600;
    border-radius: 10px;
}

:deep(.home-cta-button.linear-bg .p-button-label) {
    font-family: 'Basier Circle', sans-serif;
    font-size: 16px;
    font-weight: 600;
    line-height: 1.25;
    color: #fff;
}

:deep(.home-cta-button.linear-bg:hover:not(:disabled)) {
    background: #8a6226;
    color: #fff;
}

:deep(.home-cta-button.linear-bg:hover:not(:disabled) .p-button-label) {
    color: #fff;
}

.home-cta-button:hover {
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.4);
}

.button-disclaimer {
    font-size: 13px;
    font-weight: 300;
    line-height: 19px;
    font-family: 'Nunito', sans-serif;
    color: rgba(255, 255, 255, 1);
    margin: 0;
}

.creator-link {
    color: rgba(255, 255, 255, 1);
    transition: color 0.2s ease;
}

.creator-link:hover {
    color: #c9a646;
}

.underline {
    text-decoration: underline;
    text-underline-offset: 2px;
}

/* Modal Styles */
.auth-modal-container {
    width: 100%;
    height: 100%;
    border-radius: 14px;
    padding: 40px 20px;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
    display: flex;
    align-items: center;
    justify-content: center;
}

/* Desktop Styles */
@media (min-width: 769px) {
    .home-header {
        padding: 32px 100px;
    }

    .logo {
        width: 102px;
    }

    .nav-button {
        display: block;
    }

    .menu-button {
        display: none;
    }

    .home-content {
        padding: 0 80px 80px;
        flex: unset;
        gap: 90px;
        height: 100%;
        justify-content: center;
    }

    .text-content {
        flex: unset;
        max-width: 600px;
        margin-bottom: 0px;
        margin-top: 120px;
    }

    .cta-section {
        max-width: 600px;
    }

    .main-title {
        font-size: 48px;
        font-weight: 700;
        line-height: 50px;
        margin-bottom: 16px;
    }

    .subtitle {
        font-size: 18px;
        font-weight: 600;
        line-height: 125%;
        color: rgba(255, 255, 255, 0.85);
    }

    .home-cta-button {
        width: 310px;
        height: 54px;
    }

    .auth-modal-container {
        min-height: 500px;
        padding: 60px 20px;
    }
}
</style>
